import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, MessageCircle, ExternalLink, ShieldCheck, Users, Plus, Trash2, UserCheck, Ban, Wallet, FileSignature } from "lucide-react";
import { normalizePkMobile, buildWaLink } from "../lib/whatsappLink";
import CurrentPlanCard from "../components/CurrentPlanCard";
import PushNotificationsCard from "../components/PushNotificationsCard";
import { useShopSettings } from "../lib/useShopSettings";

export default function Settings() {
  const { user, updateShop } = useAuth();
  const isOwner = user?.role === "owner";
  const [form, setForm] = useState({
    shop_name: user?.shop_name || "",
    owner_name: user?.owner_name || "",
    shop_whatsapp_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [waError, setWaError] = useState("");
  const [blacklistMode, setBlacklistMode] = useState("warn");
  const [pricingEnabled, setPricingEnabled] = useState(false);
  const [postCreateAction, setPostCreateAction] = useState("auto_agreement");
  const { refresh: refreshShopSettings, set: setShopCache } = useShopSettings();

  useEffect(() => {
    api.get("/auth/shop").then((r) => {
      setForm((f) => ({
        ...f,
        shop_name: r.data.shop_name || f.shop_name,
        owner_name: r.data.owner_name || f.owner_name,
        shop_whatsapp_number: r.data.shop_whatsapp_number || "",
      }));
      setBlacklistMode(r.data.blacklist_enforcement_mode || "warn");
      setPricingEnabled(!!r.data.internal_pricing_enabled);
      setPostCreateAction(r.data.loan_post_create_action || "auto_agreement");
    }).catch(() => {});
  }, []);

  const updatePostCreateAction = async (next) => {
    const prev = postCreateAction;
    setPostCreateAction(next);
    try {
      await api.put("/auth/shop", { loan_post_create_action: next });
      setShopCache({ loan_post_create_action: next });
      refreshShopSettings();
      toast.success(next === "auto_agreement"
        ? "Default: Loan ke baad Agreement auto open hoga"
        : "Default: Loan ke baad Loan Page par hi rahein ge");
    } catch {
      setPostCreateAction(prev);
      toast.error("Setting save nahi hui");
    }
  };

  const togglePricing = async (next) => {
    const prev = pricingEnabled;
    setPricingEnabled(next);
    try {
      await api.put("/auth/shop", { internal_pricing_enabled: next });
      toast.success(next ? "Internal pricing ON — Loan form mein cost & profit fields dikhain ge" : "Internal pricing OFF");
    } catch {
      setPricingEnabled(prev);
      toast.error("Setting save nahi hui");
    }
  };

  const updateBlacklistMode = async (newMode) => {
    const prev = blacklistMode;
    setBlacklistMode(newMode);
    try {
      await api.put("/auth/shop", { blacklist_enforcement_mode: newMode });
      toast.success(`Blacklist mode set: ${newMode === "block" ? "BLOCK (strict)" : "WARN (override allowed)"}`);
    } catch {
      setBlacklistMode(prev); // revert on failure
      toast.error("Mode save nahi hua");
    }
  };

  if (!isOwner) {
    return (
      <div className="max-w-md mx-auto mt-20 bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="settings-denied">
        <Lock className="w-10 h-10 mx-auto text-slate-400" />
        <div className="font-display text-xl font-bold mt-3">Owner only</div>
        <p className="text-slate-500 text-sm mt-1">Contact your shop owner to change settings.</p>
      </div>
    );
  }

  const validateWa = (val) => {
    if (!val) return "";
    const digits = String(val).replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 13) return "Mobile number 10–13 digits ka hona chahiye";
    return "";
  };

  const submitProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateShop({ shop_name: form.shop_name, owner_name: form.owner_name });
      toast.success("Profile saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const submitWa = async (e) => {
    e.preventDefault();
    const err = validateWa(form.shop_whatsapp_number);
    if (err) {
      setWaError(err);
      toast.error(err);
      return;
    }
    setWaError("");
    setSaving(true);
    try {
      await updateShop({ shop_whatsapp_number: form.shop_whatsapp_number });
      toast.success("WhatsApp number saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const testWa = () => {
    const err = validateWa(form.shop_whatsapp_number);
    if (err) {
      setWaError(err);
      toast.error(err);
      return;
    }
    const link = buildWaLink(
      form.shop_whatsapp_number,
      `Test message from ${form.shop_name || "HisaabPro"} — agar aap ko ye mil gaya, WhatsApp setup tayyar hai.`,
    );
    if (link) window.open(link, "_blank", "noopener");
  };

  const normalized = normalizePkMobile(form.shop_whatsapp_number);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Settings</h1>
        <p className="text-slate-500 mt-1">Shop profile, WhatsApp, plan, aur security.</p>
      </div>

      {/* Plan summary — visible from settings for quick glance */}
      <CurrentPlanCard />

      {/* Push Notifications — manual register + test push for Android */}
      <PushNotificationsCard />

      {/* Blacklist Enforcement — quick toggle (full list at /blacklist) */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-3" data-testid="settings-blacklist-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <Ban className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <div className="font-display font-bold text-lg">Blacklist Enforcement</div>
              <p className="text-xs text-slate-500 max-w-md">
                Customer banate ya loan create karte waqt agar CNIC/Mobile blacklist mein match ho:
              </p>
            </div>
          </div>
          <a
            href="/blacklist"
            className="text-xs font-semibold text-emerald-700 hover:underline"
            data-testid="settings-bl-manage-link"
          >
            Manage list →
          </a>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => updateBlacklistMode("warn")}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${blacklistMode === "warn" ? "bg-amber-500 border-amber-500 text-white" : "bg-slate-50 text-slate-600 border-slate-200"}`}
            data-testid="settings-bl-mode-warn"
          >
            Warn (Soft)
          </button>
          <button
            type="button"
            onClick={() => updateBlacklistMode("block")}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${blacklistMode === "block" ? "bg-red-600 border-red-600 text-white" : "bg-slate-50 text-slate-600 border-slate-200"}`}
            data-testid="settings-bl-mode-block"
          >
            Block (Strict)
          </button>
        </div>
      </div>

      {/* Internal Pricing (Cost & Profit Tracking) — optional, owner-only */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-3" data-testid="settings-pricing-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <Wallet className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div>
              <div className="font-display font-bold text-lg">Internal Cost & Profit Tracking</div>
              <p className="text-xs text-slate-500 max-w-md">
                Loan create karte waqt cost price + profit % / fixed enter karein. Profit report owner ko visible hota hai — customer aur Agreement PDF par koi cost/profit nahi dikhe ga.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => togglePricing(!pricingEnabled)}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition ${pricingEnabled ? "bg-emerald-600" : "bg-slate-300"}`}
            data-testid="settings-pricing-toggle"
            aria-pressed={pricingEnabled}
          >
            <span className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition mt-0.5 ${pricingEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
        {pricingEnabled && (
          <a href="/reports/profit" className="text-xs font-semibold text-emerald-700 hover:underline inline-block" data-testid="settings-pricing-report-link">
            View Profit Report →
          </a>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="settings-loan-flow-card">
        <div className="flex items-start gap-2">
          <FileSignature className="w-5 h-5 text-emerald-600 mt-0.5" />
          <div>
            <div className="font-display font-bold text-lg">After creating a loan</div>
            <p className="text-xs text-slate-500 max-w-md">
              Loan create karne ke baad system kya kare — har retailer apna workflow choose kar sakta hai.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => updatePostCreateAction("auto_agreement")}
            data-testid="settings-post-create-auto-agreement"
            className={`text-left p-4 rounded-md border-2 transition ${postCreateAction === "auto_agreement" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
          >
            <div className="flex items-center gap-2">
              <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${postCreateAction === "auto_agreement" ? "border-emerald-500" : "border-slate-300"}`}>
                {postCreateAction === "auto_agreement" && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
              </span>
              <div className="font-semibold text-sm text-slate-900">Automatically open Agreement</div>
              <span className="ml-auto text-[10px] uppercase font-bold tracking-wider text-emerald-600">Default</span>
            </div>
            <p className="text-xs text-slate-500 mt-1.5 pl-6">
              Success dialog mein "Generate &amp; Sign Agreement" highlighted hoga.
            </p>
          </button>

          <button
            type="button"
            onClick={() => updatePostCreateAction("stay")}
            data-testid="settings-post-create-stay"
            className={`text-left p-4 rounded-md border-2 transition ${postCreateAction === "stay" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
          >
            <div className="flex items-center gap-2">
              <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${postCreateAction === "stay" ? "border-emerald-500" : "border-slate-300"}`}>
                {postCreateAction === "stay" && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
              </span>
              <div className="font-semibold text-sm text-slate-900">Stay on Loan Page</div>
            </div>
            <p className="text-xs text-slate-500 mt-1.5 pl-6">
              Success dialog mein "Skip for Now" highlighted hoga. Agreement baad mein generate kar sakte hain.
            </p>
          </button>
        </div>
      </div>

      <form onSubmit={submitProfile} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Profile</div>
        <div className="space-y-1.5">
          <Label>Shop name</Label>
          <Input required value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} data-testid="settings-shop-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Owner name</Label>
          <Input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="settings-owner-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input value={user?.email || ""} disabled />
        </div>
        <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-save-btn">
          {saving ? "Saving..." : "Save profile"}
        </Button>
      </form>

      <form onSubmit={submitWa} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            <div>
              <div className="font-display font-bold text-lg">WhatsApp Business Settings</div>
              <p className="text-xs text-slate-500">
                Customers ko reminders aap ke apne WhatsApp Business se chalein gay (deep-link). Koi API ya monthly fee nahi.
              </p>
            </div>
          </div>
          {normalized && !waError ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="wa-status-connected">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-300 text-red-700 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="wa-status-disconnected">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              Not Connected
            </span>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>WhatsApp Business Number <span className="text-red-500">*</span></Label>
          <Input
            placeholder="03001234567"
            value={form.shop_whatsapp_number}
            onChange={(e) => {
              setForm({ ...form, shop_whatsapp_number: e.target.value });
              if (waError) setWaError(validateWa(e.target.value));
            }}
            data-testid="settings-wa-input"
            inputMode="numeric"
          />
          {waError && <div className="text-xs text-red-600 font-semibold" data-testid="settings-wa-error">{waError}</div>}
          {normalized && !waError && (
            <div className="text-xs text-slate-500">
              wa.me par yahan jaye ga: <span className="font-mono text-slate-700">+{normalized}</span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="settings-wa-save">
            <ShieldCheck className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Number"}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!normalized || !!waError}
            onClick={() => normalized && window.open(`https://wa.me/${normalized}`, "_blank")}
            data-testid="settings-wa-open"
          >
            <ExternalLink className="w-4 h-4 mr-1.5 text-emerald-600" /> Open WhatsApp
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!normalized || !!waError}
            onClick={testWa}
            data-testid="settings-wa-test"
          >
            <MessageCircle className="w-4 h-4 mr-1.5 text-emerald-600" /> Send Test Message
          </Button>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
          <div className="font-semibold text-slate-700">Default Reminder Message</div>
          Customers ke reminder templates aap <a href="/whatsapp" className="text-emerald-700 font-semibold underline">WhatsApp page</a> se edit kar sakte hain (Before due / Due today / Overdue ke alag templates).
        </div>
      </form>

      {isOwner && <RecoveryAgentsCard />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Recovery Agents — owner manages sub-users for field recovery
// ─────────────────────────────────────────────────────────────
function RecoveryAgentsCard() {
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", recovery_zone: "", mobile: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/agents").then((r) => setAgents(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/agents", form);
      toast.success(`Agent ${form.name} added`);
      setForm({ name: "", email: "", password: "", recovery_zone: "", mobile: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}? Their assigned customers will be unassigned.`)) return;
    try {
      await api.delete(`/agents/${id}`);
      toast.success("Agent removed");
      load();
    } catch (err) {
      toast.error("Failed");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="agents-card">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-emerald-600" />
        <div>
          <div className="font-display font-bold text-lg">Recovery Agents</div>
          <p className="text-xs text-slate-500">
            Field agents jo overdue customers se collect karein gay. Har agent ka apna login + zone hota hai.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end" data-testid="agent-add-form">
        <div className="space-y-1.5">
          <Label className="text-xs">Name</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ahmed Khan" data-testid="agent-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Email (login)</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ahmed@shop.pk" data-testid="agent-email-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Password</Label>
          <Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 chars" data-testid="agent-pwd-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Recovery Zone</Label>
          <Input value={form.recovery_zone} onChange={(e) => setForm({ ...form, recovery_zone: e.target.value })} placeholder="Zone 5 - Gulshan" data-testid="agent-zone-input" />
        </div>
        <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="agent-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </form>

      <div className="border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Name</th>
              <th className="text-left px-3 py-2 font-semibold">Email</th>
              <th className="text-left px-3 py-2 font-semibold">Zone</th>
              <th className="text-left px-3 py-2 font-semibold">Assigned</th>
              <th className="text-right px-3 py-2 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {agents.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No recovery agents yet.</td></tr>}
            {agents.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`agent-row-${a.id}`}>
                <td className="px-3 py-2 font-medium flex items-center gap-1"><UserCheck className="w-3.5 h-3.5 text-emerald-600" />{a.name}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{a.email}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{a.recovery_zone || "—"}</td>
                <td className="px-3 py-2 tabular">{a.assigned_customers}</td>
                <td className="px-3 py-2 text-right">
                  <button type="button" onClick={() => remove(a.id, a.name)} className="text-red-600 hover:text-red-800" title="Remove" data-testid={`agent-remove-${a.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
